import { CATEGORY_ICONS, fmt, hasValue, parseKip, parseQty, type Product } from '@k2pos/core';
import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AddValueSheet } from '@/components/ui/AddValueSheet';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { UnitChip } from '@/components/ui/Chip';
import { ConfirmSheet } from '@/components/ui/ConfirmSheet';
import { Field } from '@/components/ui/Field';
import { Mono, Text } from '@/components/ui/Text';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { CategoryGlyph, IconPlus } from '@/components/icons';
import { toProductDataUrl } from '@/lib/image';
import { useBackHandling, useGoBack } from '@/hooks/useNav';
import { useMoneyAction } from '@/hooks/useMoneyAction';
import { useProductImages } from '@/hooks/useProductImages';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './EditProduct.module.css';

/**
 * ແກ້ຂໍ້ມູນສິນຄ້າ — name, category, prices, the low-stock threshold, and
 * retiring.
 *
 * Editing here cannot disturb anything already sold: price and cost are copied
 * onto the entry line at the moment of sale, so a price change moves what the
 * shop charges from now on and leaves last month's takings and margin alone.
 */
export function EditProduct() {
  const { id = '' } = useParams();
  const goBack = useGoBack('/stock');
  const navigate = useNavigate();

  const catalog = usePos((s) => s.catalog);
  const retired = usePos((s) => s.retired);
  const categories = usePos((s) => s.categories);
  const units = usePos((s) => s.units);
  const updateProduct = usePos((s) => s.updateProduct);
  const addUnitOption = usePos((s) => s.addUnitOption);
  const addCategory = usePos((s) => s.addCategory);

  const product = useMemo(
    () => [...catalog, ...retired].find((p) => p.id === id),
    [catalog, retired, id],
  );

  const [name, setName] = useState(product?.name ?? '');
  const [cat, setCat] = useState(product?.cat ?? '');
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries((product?.units ?? []).map((u) => [u.unit, String(u.price)])),
  );
  const [lowStock, setLowStock] = useState(
    product?.lowStockAt === undefined ? '' : String(product.lowStockAt),
  );
  const [newUnit, setNewUnit] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newPerBase, setNewPerBase] = useState('');
  const [counted, setCounted] = useState('');
  const [broken, setBroken] = useState('');
  /** Which confirm is open. Only one can be, so one piece of state. */
  const [asking, setAsking] = useState<'retire' | 'count' | 'break' | null>(null);
  /** The add-to-the-shop's-vocabulary sheet, same as the one on ເພີ່ມສິນຄ້າ. */
  const [adding, setAdding] = useState<'unit' | 'category' | null>(null);
  useBackHandling(false, (asking ? 1 : 0) + (adding ? 1 : 0), () => {
    setAsking(null);
    setAdding(null);
  });

  const save = useMemo(
    () => async () => {
      // `parseKip`/`parseQty` return null on anything they cannot read. Falling
      // back to the current value keeps a half-typed field from silently
      // repricing the product to something nobody chose.
      const existing = (product?.units ?? []).map((u) => ({
        unit: u.unit,
        price: parseKip(prices[u.unit] ?? '') ?? u.price,
      }));

      const addedPrice = parseKip(newPrice);
      const added =
        newUnit.trim() && addedPrice !== null
          ? [
              {
                unit: newUnit.trim(),
                price: addedPrice,
                ...(parseQty(newPerBase) !== null ? { perBase: parseQty(newPerBase)! } : {}),
              },
            ]
          : [];

      await updateProduct(id, {
        name: name.trim(),
        cat,
        // Blank means "stop warning me", which is a different request from
        // leaving the field alone. The server tells the two apart.
        lowStockAt: lowStock.trim() === '' ? null : parseQty(lowStock),
        units: [...existing, ...added],
      });
    },
    [id, name, cat, lowStock, prices, newUnit, newPrice, newPerBase, product, updateProduct],
  );

  const { run, busy, error, dismiss } = useMoneyAction(save, () => navigate(-1));

  const countStock = usePos((s) => s.countStock);
  const adjustStock = usePos((s) => s.adjustStock);

  const doCount = useMemo(
    () => async () => {
      const value = parseQty(counted);
      if (value === null) return;
      await countStock(id, value);
      setCounted('');
    },
    [id, counted, countStock],
  );

  const doBreak = useMemo(
    () => async () => {
      const value = parseQty(broken);
      if (!value) return;
      // Negative: breakage removes stock. The shopkeeper types how many broke.
      await adjustStock(id, -value, 'breakage');
      setBroken('');
    },
    [id, broken, adjustStock],
  );

  const {
    run: runCount,
    busy: counting,
    error: countError,
    dismiss: dismissCount,
  } = useMoneyAction(doCount, () => setAsking(null));
  const {
    run: runBreak,
    busy: breaking,
    error: breakError,
    dismiss: dismissBreak,
  } = useMoneyAction(doBreak, () => setAsking(null));

  const retireProduct = usePos((s) => s.retireProduct);
  const doRetire = useMemo(
    () => async (restore: boolean) => {
      await retireProduct(id, restore);
    },
    [id, retireProduct],
  );
  // Through useMoneyAction like every other write: a refusal here used to be a
  // swallowed promise rejection, so the screen sat there looking like nothing
  // had been asked of it.
  const {
    run: runRetire,
    busy: retiring,
    error: retireError,
    dismiss: dismissRetire,
  } = useMoneyAction(doRetire, () => {
    setAsking(null);
    navigate(-1);
  });

  if (!product) {
    return (
      <div className="screen col">
        <AppBar title="ແກ້ຂໍ້ມູນສິນຄ້າ" onBack={goBack} />
        <div className={styles.body}>
          <Text size={13} color={colors.textMuted}>
            ບໍ່ພົບສິນຄ້ານີ້
          </Text>
        </div>
      </div>
    );
  }

  const isRetired = product.retiredAt !== undefined;
  const tracked = product.remaining !== undefined;
  const base = product.units[0]?.unit ?? '';

  return (
    <div className="screen col">
      <AppBar title="ແກ້ຂໍ້ມູນສິນຄ້າ" onBack={goBack} />
      {error ? <ErrorBanner message={error} onDismiss={dismiss} /> : null}
      {countError ? <ErrorBanner message={countError} onDismiss={dismissCount} /> : null}
      {breakError ? <ErrorBanner message={breakError} onDismiss={dismissBreak} /> : null}
      {retireError ? <ErrorBanner message={retireError} onDismiss={dismissRetire} /> : null}

      <div className={`${styles.body} vscroll`}>
        {isRetired ? (
          <Card className={styles.notice}>
            <Text size={12.5} weight={700} color={colors.skyDeep}>
              ສິນຄ້ານີ້ເລີກຂາຍແລ້ວ
            </Text>
            <Text size={11.5} color={colors.textMuted}>
              ບໍ່ປາກົດໃນໜ້າຂາຍ ແຕ່ໃບບິນເກົ່າຍັງຄົບ
            </Text>
          </Card>
        ) : null}

        <ProductImageField product={product} />

        <div className={styles.field}>
          <Text size={12.5} color={colors.textMuted}>
            ຊື່ສິນຄ້າ
          </Text>
          <Field value={name} onChange={setName} placeholder="ຊື່ສິນຄ້າ" />
        </div>

        <div className={styles.field}>
          <Text size={12.5} color={colors.textMuted}>
            ໝວດ
          </Text>
          <div className={`${styles.chips} hscroll`}>
            {categories.map((name) => (
              <UnitChip key={name} unit={name} selected={cat === name} onPress={() => setCat(name)} />
            ))}
            <button type="button" onClick={() => setAdding('category')} className={styles.addChip}>
              <IconPlus size={13} />
            </button>
          </div>
        </div>

        <Text weight={800} size={12} color={colors.textMuted}>
          ລາຄາຂາຍ
        </Text>
        {product.units.map((u) => (
          <Card key={u.unit} className={styles.unitRow}>
            <div className="grow col">
              <Text weight={700} size={13} align="left">
                {u.unit}
              </Text>
              {u.perBase !== undefined && u.perBase !== 1 ? (
                <Text size={10.5} color={colors.textMuted} align="left">
                  1 {u.unit} = {fmt(u.perBase)} {base}
                </Text>
              ) : null}
            </div>
            <Field
              kind="kip"
              value={prices[u.unit] ?? String(u.price)}
              onChange={(v) => setPrices((p) => ({ ...p, [u.unit]: v }))}
              className={styles.priceField}
            />
          </Card>
        ))}

        {/* Add-only: a unit named on an old bill has to keep resolving, so
            there is no remove here and none on the server either. */}
        <Card className={styles.addUnit}>
          <Text size={12.5} weight={700}>
            ເພີ່ມຫົວໜ່ວຍຂາຍ
          </Text>
          <div className={`${styles.chips} hscroll`}>
            {units
              .filter((u) => !product.units.some((existing) => existing.unit === u.name))
              .map((u) => (
                <UnitChip
                  key={u.name}
                  unit={u.name}
                  selected={newUnit === u.name}
                  onPress={() => setNewUnit(newUnit === u.name ? '' : u.name)}
                />
              ))}
            {/* A unit the shop has never used before is exactly the case that
                brings someone to this screen — ໂຕນ on the cement they had only
                ever sold by the bag. Sending them to /settings and back would
                lose the half-typed price above. */}
            <button type="button" onClick={() => setAdding('unit')} className={styles.addChip}>
              <IconPlus size={13} />
            </button>
          </div>
          {newUnit ? (
            <>
              <div className={styles.field}>
                <Text size={11.5} color={colors.textMuted}>
                  ລາຄາຕໍ່ 1 {newUnit}
                </Text>
                <Field kind="kip" value={newPrice} onChange={setNewPrice} placeholder="0" />
              </div>
              <div className={styles.field}>
                <Text size={11.5} color={colors.textMuted}>
                  1 {newUnit} ເທົ່າກັບຈັກ {base}?
                </Text>
                <Field kind="qty" value={newPerBase} onChange={setNewPerBase} placeholder="20" />
                {/* Without this a sale of 0.5 ໂຕນ takes half a bag off stock
                    instead of ten. */}
                <Text size={10.5} color={colors.textMuted}>
                  ຕ້ອງໃສ່ ບໍ່ດັ່ງນັ້ນສະຕັອກຈະຫັກຜິດ
                </Text>
              </div>
            </>
          ) : null}
        </Card>

        <div className={styles.field}>
          <Text size={12.5} color={colors.textMuted}>
            ເຕືອນເມື່ອເຫຼືອໜ້ອຍກວ່າ ({base})
          </Text>
          <Field kind="qty" value={lowStock} onChange={setLowStock} placeholder="ບໍ່ເຕືອນ" />
        </div>

        <Text weight={800} size={12} color={colors.textMuted}>
          ສະຕັອກ
        </Text>
        <Card className={styles.addUnit}>
          {tracked ? (
            <Text size={12.5}>
              ຍັງເຫຼືອ <Mono size={13} weight={700}>{fmt(product.remaining ?? 0)}</Mono> {base}
            </Text>
          ) : (
            <>
              <Text size={12.5} weight={700}>
                ສິນຄ້ານີ້ບໍ່ໄດ້ນັບສະຕັອກ
              </Text>
              <Text size={11} color={colors.textMuted}>
                ໃສ່ຈຳນວນທີ່ມີຢູ່ຈິງແລ້ວກົດນັບ ຈາກນັ້ນລະບົບຈະເລີ່ມນັບໃຫ້
              </Text>
            </>
          )}

          <div className={styles.field}>
            <Text size={11.5} color={colors.textMuted}>
              {tracked ? `ນັບໄດ້ຈິງຈັກ ${base}?` : `ມີຢູ່ຈິງຈັກ ${base}?`}
            </Text>
            <Field kind="qty" value={counted} onChange={setCounted} placeholder="0" />
          </div>
          {/* The shopkeeper enters what is on the shelf; the server works out
              the difference under a lock. Computing it here would read the
              figure, let the cashier sell three in the meantime, and post a
              difference against a number that had moved. */}
          <Button
            label={tracked ? 'ບັນທຶກຈຳນວນທີ່ນັບໄດ້' : 'ເລີ່ມນັບສະຕັອກ'}
            variant="accent"
            disabled={busy || parseQty(counted) === null}
            onPress={() => setAsking('count')}
          />

          {tracked ? (
            <>
              <div className={styles.field}>
                <Text size={11.5} color={colors.textMuted}>
                  ຂອງແຕກ / ເສຍຫາຍ ຈັກ {base}?
                </Text>
                <Field kind="qty" value={broken} onChange={setBroken} placeholder="0" />
              </div>
              <Button
                label="ບັນທຶກຂອງແຕກ"
                variant="outline"
                disabled={busy || !parseQty(broken)}
                onPress={() => setAsking('break')}
              />
            </>
          ) : null}

          <Text size={10.5} color={colors.textMuted}>
            ການນັບຍ້າຍແຕ່ເຄື່ອງ ບໍ່ຍ້າຍເງິນ — ບໍ່ໄປປົນໃນກຳໄລຂາດທຶນ
          </Text>
        </Card>

        <Button
          label={busy ? 'ກຳລັງບັນທຶກ…' : 'ບັນທຶກ'}
          disabled={busy || !name.trim()}
          onPress={() => run(undefined)}
        />

        {/* Never a delete. Every bill that names this product would be orphaned,
            and the ledger has to stay readable. */}
        <Button
          label={isRetired ? 'ເອົາກັບມາຂາຍ' : 'ເລີກຂາຍສິນຄ້ານີ້'}
          variant="outline"
          disabled={busy || retiring}
          onPress={() => setAsking('retire')}
        />
        <Text size={11} color={colors.textMuted}>
          ເລີກຂາຍແລ້ວຈະບໍ່ປາກົດໃນໜ້າຂາຍ ແຕ່ໃບບິນ ແລະ ລາຍງານເກົ່າຍັງຄົບທຸກຢ່າງ
        </Text>
      </div>

      <AddValueSheet
        open={adding !== null}
        title={adding === 'unit' ? 'ເພີ່ມໜ່ວຍຂາຍ' : 'ເພີ່ມໝວດສິນຄ້າ'}
        placeholder={adding === 'unit' ? 'ເຊັ່ນ ແກັດ' : 'ເຊັ່ນ ສີທາ'}
        askFractional={adding === 'unit'}
        validate={(v) =>
          hasValue(adding === 'unit' ? units.map((u) => u.name) : categories, v)
            ? 'ມີຢູ່ແລ້ວ'
            : null
        }
        // Added and selected in one tap, same as ເພີ່ມສິນຄ້າ: the owner is
        // mid-entry and adding the value only to use it right now.
        onAdd={(value, fractional) => {
          if (adding === 'unit') {
            void addUnitOption({ name: value, step: fractional ? 0.5 : 1 });
            setNewUnit(value);
          } else {
            void addCategory(value);
            setCat(value);
          }
          setAdding(null);
        }}
        onClose={() => setAdding(null)}
      />

      <ConfirmSheet
        open={asking === 'retire'}
        title={isRetired ? 'ເອົາສິນຄ້ານີ້ກັບມາຂາຍບໍ?' : 'ເລີກຂາຍສິນຄ້ານີ້ບໍ?'}
        subject={
          <>
            <Text weight={800} size={13.5} align="left">
              {product.name}
            </Text>
            {tracked ? (
              <Text size={11} color={colors.textMuted} align="left">
                ຍັງເຫຼືອ {fmt(product.remaining ?? 0)} {base}
              </Text>
            ) : null}
          </>
        }
        message={
          isRetired
            ? 'ຈະກັບມາປາກົດໃນໜ້າຂາຍຄືເກົ່າ'
            : 'ຈະບໍ່ປາກົດໃນໜ້າຂາຍອີກ ແຕ່ໃບບິນ ແລະ ລາຍງານເກົ່າຍັງຄົບ · ເອົາກັບມາຂາຍພາຍຫຼັງໄດ້'
        }
        confirmLabel={isRetired ? 'ເອົາກັບມາຂາຍ' : 'ເລີກຂາຍ'}
        busy={retiring}
        onConfirm={() => runRetire(isRetired)}
        onClose={() => setAsking(null)}
      />

      <ConfirmSheet
        open={asking === 'count'}
        title={tracked ? 'ບັນທຶກຈຳນວນທີ່ນັບໄດ້ບໍ?' : 'ເລີ່ມນັບສະຕັອກບໍ?'}
        subject={
          <>
            <Text weight={800} size={13.5} align="left">
              {product.name}
            </Text>
            <Text size={11.5} color={colors.textMuted} align="left">
              ຈະຕັ້ງເປັນ <Mono weight={700} size={13}>{counted}</Mono> {base}
              {tracked ? ` · ດຽວນີ້ລະບົບນັບໄວ້ ${fmt(product.remaining ?? 0)} ${base}` : ''}
            </Text>
          </>
        }
        message="ສ່ວນຕ່າງຈະລົງເປັນລາຍການປັບສະຕັອກ · ຍ້າຍແຕ່ເຄື່ອງ ບໍ່ຍ້າຍເງິນ"
        confirmLabel="ບັນທຶກ"
        busy={counting}
        onConfirm={() => runCount(undefined)}
        onClose={() => setAsking(null)}
      />

      <ConfirmSheet
        open={asking === 'break'}
        title="ບັນທຶກຂອງແຕກບໍ?"
        subject={
          <>
            <Text weight={800} size={13.5} align="left">
              {product.name}
            </Text>
            <Text size={11.5} color={colors.textMuted} align="left">
              ຫັກອອກ <Mono weight={700} size={13}>{broken}</Mono> {base}
            </Text>
          </>
        }
        message="ຫັກອອກຈາກສະຕັອກເລີຍ · ຖ້າໃສ່ຜິດ ຕ້ອງນັບສະຕັອກໃໝ່ເພື່ອແກ້"
        confirmLabel="ບັນທຶກຂອງແຕກ"
        busy={breaking}
        onConfirm={() => runBreak(undefined)}
        onClose={() => setAsking(null)}
      />
    </div>
  );
}

/**
 * The product's picture: take one, see it, replace or remove it.
 *
 * Optional on purpose. A shop with three hundred products will photograph the
 * ten it sells most and nothing else, and the sell screen falls back to the
 * category glyph — so this never becomes a field the shopkeeper has to fill in
 * before the product works.
 *
 * The file input carries no `capture` attribute, so Android shows its own
 * chooser and both ways in work: the camera for someone standing in front of
 * the shelf, the gallery for a picture already taken. `capture` looked like the
 * helpful default and was the opposite — Chrome went straight to the camera and
 * left no way to reach an existing photo at all.
 */
function ProductImageField({ product }: { product: Product }) {
  const images = useProductImages();
  const image = images[product.id];
  const setImage = usePos((s) => s.setProductImage);
  const picker = useRef<HTMLInputElement>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const commit = useMemo(
    () => (next: string | null) => setImage(product.id, next),
    [product.id, setImage],
  );
  const { run, busy, error, dismiss } = useMoneyAction(commit);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setFailed(null);
    try {
      run(await toProductDataUrl(file));
    } catch {
      setFailed('ອ່ານຮູບນີ້ບໍ່ໄດ້ · ລອງຮູບ PNG ຫຼື JPG');
    }
  };

  return (
    <>
      <ErrorBanner
        message={error ?? failed}
        onDismiss={() => (error ? dismiss() : setFailed(null))}
      />

      <div className={styles.imageRow}>
        <div className={styles.imageBox}>
          {image ? (
            <img src={image} alt="" className={styles.imagePhoto} />
          ) : (
            <CategoryGlyph
              icon={CATEGORY_ICONS[product.cat] ?? 'grid'}
              size={28}
              className={styles.imageGlyph}
            />
          )}
        </div>

        <div className="grow col">
          <Text size={12.5} color={colors.textMuted} align="left">
            ຮູບສິນຄ້າ · ບໍ່ບັງຄັບ
          </Text>
          <Text size={10.5} color={colors.textFaint} align="left">
            ຂຶ້ນໃນໜ້າຂາຍແບບຕາຕະລາງ · ບໍ່ມີກໍ່ໃຊ້ຮູບໝວດແທນ
          </Text>
          <div className={styles.imageActions}>
            <Button
              label={busy ? 'ກຳລັງອັບໂຫລດ…' : image ? 'ປ່ຽນຮູບ' : 'ຖ່າຍ / ເລືອກຮູບ'}
              variant="outline"
              size={12}
              className="grow"
              disabled={busy}
              onPress={() => picker.current?.click()}
            />
            {image ? (
              <Button
                label="ເອົາອອກ"
                variant="outline"
                size={12}
                className="grow"
                disabled={busy}
                onPress={() => run(null)}
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* No `capture` attribute. It reads as a hint and Android treats it as an
          order: Chrome opens the camera directly and the gallery becomes
          unreachable, so a picture already taken of the product could not be
          used. Without it the normal chooser appears — ກ້ອງ, ຮູບພາບ, ໄຟລ໌ — and
          the camera is still one tap away. */}
      <input
        ref={picker}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className={styles.hiddenInput}
        onChange={(e) => {
          void pick(e.target.files?.[0]);
          // Cleared so picking the same file twice still fires a change.
          e.target.value = '';
        }}
      />
    </>
  );
}
